import React, { useCallback, useEffect, useMemo, useState } from 'react'
import 'reactflow/dist/base.css'
import { ReactComponent as DownloadIcon } from '../../../../../../images/icons/action_download.svg'
import {
  Button,
  ChevronLeft,
  DateRangePicker,
  StatsCard,
  Table,
  TabMenu,
  Toast,
} from '@demandlocal/components'
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useLocation,
  useSearchParams,
  Link,
  useNavigate,
} from 'react-router-dom'
import { formatCurrency, formatNumber } from '../../../../../../lib/utils/formatNumber'
import { useGetThirdPartyAudienceById } from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceById'
import PolarAreaChart from '../../../../../../components/Charts/PolarAreaChart'
import { take } from 'lodash'
import HorizontalBarChart from '../../../../../../components/Charts/HorizontalBarChart'
import { useDownloadPDFReport } from '../../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { GA4 } from '../../../../../../lib/utils/ga4'
import mtm from '../../../../../../lib/utils/mtm'
import {
  AudienceMetrics,
  useGetThirdPartyAudienceReport,
} from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceReport'
import { VendorAudience } from '../../../../../../lib/types/Audience'
import { DateRange } from '../../../../../../lib/types/DateRange'
import { useDateRange } from '../../../../../../store/zustand/dateRangeStore'
import PageHeader from '../../../../../../components/PageHeader/PageHeader'
import { AnimatePresence, motion } from 'framer-motion'
import dayjs from 'dayjs'
import { useGetThirdPartyAudienceConversions } from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceConversions'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { useDealership } from '../../../../../../store/zustand/dealershipStore'

const columnHelper = createColumnHelper<{
  campaign: string
  client_campaign_id: number
  product_code: string
  impressions: number
  clicks: number
  views: number
  total_conversions_1: number
}>()

const AudienceTemplate = (): JSX.Element => {
  const { audience_id } = useParams()
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const location = useLocation()
  const reportType = location.pathname.split('/').pop()

  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange, setDateRange } = useDateRange()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const {
    data: audience,
    isLoading,
    isPreviousData,
  } = useGetThirdPartyAudienceById(audience_id)
  const {
    data: audienceMetrics,
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReport,
  } = useGetThirdPartyAudienceReport({
    audience_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const formatDateRange = useCallback(
    (dateRange: DateRange) => {
      const formattedDateRange: DateRange = {
        dateRange: {
          startDate: dayjs(dateRange.dateRange.startDate)
            .startOf('month')
            .format('YYYY-MM-DD'),
          endDate: dayjs(dateRange.dateRange.endDate).endOf('month').format('YYYY-MM-DD'),
        },
      }
      setFilters((prev: DateRange) => ({
        ...prev,
        ...formattedDateRange,
      }))
      setDateRange(formattedDateRange)
    },
    [setDateRange],
  )

  useEffect(() => {
    if (!isLoading) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoading])

  const onDateRangeChange = useCallback(
    (dateRange: DateRange) => {
      formatDateRange(dateRange)
    },
    [formatDateRange],
  )

  return (
    <div className={'flex flex-1 flex-col h-full gap-3'} id={'content'}>
      <div className="flex flex-row justify-between px-10 py-5">
        <div className="flex flex-row gap-3 items-center">
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.audiences.programmatic', { dealership: dealership }))
            }}>
            {/* @ts-ignore */}
            <ChevronLeft className="fill-web-gray" />
          </div>
          <h2 className="font-bold text-xl">{audience?.vendor_audience_name}</h2>
        </div>
        <AnimatePresence>
          <motion.div className="flex flex-row gap-3 items-center">
            <div className={'print:hidden flex flex-row gap-2 items-center'}>
              {reportType === 'data' && (
                <DateRangePicker filters={filters} onChange={onDateRangeChange} />
              )}
              <Button
                disabled={isLoading || isDownloadingReport}
                leftSlot={<DownloadIcon className={'fill-white'} />}
                animate={false}
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Generating report...')
                  GA4.sendEvent({
                    event_name: 'export_pdf',
                    event_params: {
                      campaign_type: 'ttd_audience',
                      campaign_id: audience?.audience_id,
                    },
                  })
                  mtm.sendEvent('Export', 'PDF', 'ttd_audience', audience?.audience_id)
                  await downloadReport(undefined, {
                    async onSuccess(data) {
                      toast.success('Report generated successfully', { id: toastId })
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${audience?.vendor_audience_name}_insights.pdf`
                      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                      a.click()
                      a.remove()
                    },
                  })
                }}>
                Export as PDF
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mb-10'} />
        <Routes>
          <Route
            path={'data'}
            element={
              <AudienceData
                audience={audience}
                audienceMetrics={audienceMetrics}
                isLoading={isLoading}
                isPreviousData={isPreviousData}
              />
            }
          />
          <Route path="*" element={<Navigate to={'data'} replace={true} />} />
        </Routes>
      </div>
    </div>
  )
}

const AudienceData = ({
  audience,
  audienceMetrics,
  isLoading,
  isPreviousData,
}: {
  audience?: VendorAudience
  audienceMetrics?: AudienceMetrics
  isLoading: boolean
  isPreviousData: boolean
}): JSX.Element => {
  const { dateRange } = useDateRange()
  const { t } = useTranslation('routes')
  const dealership = useDealership()

  const totalImpressions = useMemo(
    () =>
      audienceMetrics?.dailyMetrics?.reduce((acc, cur) => (acc += cur.impressions), 0) ||
      0,
    [audienceMetrics?.dailyMetrics],
  )
  const totalClicks = useMemo(
    () =>
      audienceMetrics?.dailyMetrics?.reduce((acc, cur) => (acc += cur.clicks), 0) || 0,
    [audienceMetrics?.dailyMetrics],
  )
  const totalViews = useMemo(
    () => audienceMetrics?.dailyMetrics?.reduce((acc, cur) => (acc += cur.views), 0) || 0,
    [audienceMetrics?.dailyMetrics],
  )

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor('campaign', {
        header: () => 'Campaign',
        cell: (info) => (
          <Link
            className={'font-bold text-xs text-primary-persian-blue'}
            to={t('dealership.campaigns.report', {
              dealership,
              campaign: {
                product_code: info.row.original.product_code,
                client_campaign_id: info.row.original.client_campaign_id,
                start_date: dateRange.dateRange.startDate,
                end_date: dateRange.dateRange.endDate,
              },
            })}>
            {info.getValue()}
          </Link>
        ),
      }),

      columnHelper.accessor('impressions', {
        header: () => 'Impressions',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      columnHelper.accessor('clicks', {
        header: () => 'Clicks',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      columnHelper.accessor('views', {
        header: () => 'Views',
        cell: (info) => formatNumber(info.getValue()),
      }),
      columnHelper.display({
        id: 'conversions',
        header: () => 'Conversions',
        cell: ({ row }) => formatNumber(row.original.total_conversions_1 || 0),
      }),
    ]
    return columns
  }, [dateRange.dateRange.endDate, dateRange.dateRange.startDate, dealership, t])
  return (
    <div className={'flex flex-col gap-10 pb-8'}>
      <div className="flex flex-col gap-5">
        <motion.div
          layout={'preserve-aspect'}
          className={`grid grid-cols-3 gap-5 min-w-max w-full `}>
          <StatsCard
            title={'Total Impressions'}
            content={formatNumber(
              audienceMetrics?.dailyMetrics?.reduce(
                (acc, cur) => (acc += cur.impressions),
                0,
              ) || 0,
            )}
          />
          <StatsCard
            title={totalClicks > 0 ? 'Total Clicks' : 'Total views'}
            content={formatNumber(totalClicks > 0 ? totalClicks : totalViews || 0)}
          />
          <StatsCard
            title={totalClicks > 0 ? 'CTR' : 'VTR'}
            content={`${
              Math.round(
                totalClicks > 0
                  ? (1000 * totalClicks) / totalImpressions
                  : (1000 * totalViews) / totalImpressions,
              ) / 10 || 0
            }%`}
          />
        </motion.div>
        <motion.div
          layout={'preserve-aspect'}
          className={`grid grid-cols-2 gap-5 min-w-max w-full `}>
          <StatsCard
            title={'People'}
            content={formatNumber(
              audience?.vendor_audience_details?.estimatedSize?.max || 0,
            )}
          />
          <StatsCard
            title={'Households'}
            content={formatNumber(
              audience?.vendor_audience_details?.estimatedSize?.min || 0,
            )}
          />
        </motion.div>
      </div>
      <Toast
        className="gap-3 p-5 font-bold"
        variant={'info'}
        text={
          "Audience insights are based on 'affinity'. Affinity is a measure of how likely customers in the input audience are to belong to this segment. An affinity of 5 indicates that customers in the input audience are 5 times as likely to belong to this segment than the average of customers on Amazon."
        }
      />
      <div className={'flex flex-col gap-8'}>
        <div className="flex flex-col gap-3">
          <h2 className={'font-bold text-md'}>Demographics</h2>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Gender</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={
                    audience?.vendor_audience_details?.demographics?.gender?.map(
                      (gender) => {
                        return {
                          label: gender.attribute,
                          value: gender.insightMetrics.affinity,
                        }
                      },
                    ) ?? []
                  }
                  isLoading={isLoading}
                  chartScale="linear"
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Education</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={
                    audience?.vendor_audience_details?.demographics?.education
                      ?.map((data) => {
                        return {
                          label: data.attribute,
                          value: data.insightMetrics.affinity,
                        }
                      })
                      .sort((a, b) => (a > b ? -1 : 1)) ?? []
                  }
                  isLoading={isLoading}
                  chartScale="linear"
                />
              </div>
            </div>
          </div>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Income</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    audience?.vendor_audience_details?.demographics?.income
                      ?.map((data) => {
                        return {
                          label: `${formatCurrency(data.range.min, data.currency)}${
                            data.range.max
                              ? ` - ${formatCurrency(data.range.max, data.currency)}`
                              : '+'
                          }`,
                          value: data.insightMetrics.affinity,
                          min: data.range.min,
                        }
                      })
                      .sort((a, b) => (a.min > b.min ? -1 : 1)) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Age</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    audience?.vendor_audience_details?.demographics?.age
                      ?.map((data) => {
                        return {
                          label: `${data.range.min}${
                            data.range.max ? ` - ${data.range.max}` : '+'
                          }`,
                          value: data.insightMetrics.affinity,
                          min: data.range.min,
                        }
                      })
                      .sort((a, b) => (a.min > b.min ? -1 : 1)) ?? []
                  }
                />
              </div>
            </div>
          </div>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Property Ownership</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={
                    audience?.vendor_audience_details?.demographics?.propertyOwnership?.map(
                      (data) => {
                        return {
                          label: data.attribute,
                          value: data.insightMetrics.affinity,
                        }
                      },
                    ) ?? []
                  }
                  isLoading={isLoading}
                  chartScale="linear"
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Children age</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    audience?.vendor_audience_details?.demographics?.childrenAge
                      ?.map((data) => {
                        return {
                          label: `${data.range.min}${
                            data.range.max ? ` - ${data.range.max}` : '+'
                          }`,
                          value: data.insightMetrics.affinity,
                          min: data.range.min,
                        }
                      })
                      .sort((a, b) => (a.min > b.min ? -1 : 1)) ?? []
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <h2 className={'font-bold text-md'}>Top Categories Purchased</h2>
          <div className={'flex grid grid-cols-1 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top sellers</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.topCategoriesPurchased
                        ?.sort((a, b) =>
                          a?.insightMetrics?.affinity > b?.insightMetrics?.affinity
                            ? -1
                            : 1,
                        )
                        ?.map((data) => {
                          return {
                            label: data.name,
                            value: data?.insightMetrics?.affinity,
                          }
                        }),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <h2 className={'font-bold text-md'}>Prime Video Insights</h2>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top movies</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.primeVideoInsights?.movies?.map(
                        (data) => {
                          return {
                            label: data.movie,
                            value: data?.insight?.affinity,
                          }
                        },
                      ),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top genres</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.primeVideoInsights?.genres?.map(
                        (data) => {
                          return {
                            label: data.genre,
                            value: data?.insight?.affinity,
                          }
                        },
                      ),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
          </div>
          <div className={'flex grid grid-cols-3 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top series</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.primeVideoInsights?.series?.map(
                        (data) => {
                          return {
                            label: data.series,
                            value: data?.insight?.affinity,
                          }
                        },
                      ),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top actors</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.primeVideoInsights?.actors?.map(
                        (data) => {
                          return {
                            label: data.actor,
                            value: data?.insight?.affinity,
                          }
                        },
                      ),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
                <span>Top directors</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  data={
                    take(
                      audience?.vendor_audience_details?.primeVideoInsights?.directors?.map(
                        (data) => {
                          return {
                            label: data.director,
                            value: data?.insight?.affinity,
                          }
                        },
                      ),
                      5,
                    ) ?? []
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className={'flex flex-col gap-4'}>
          <span className={'font-bold text-primary-xiketic text-sm'}>
            Audience performance
          </span>
          <Table
            emptyLabel={'No data found'}
            columns={columns}
            filters={{
              audience_id: audienceMetrics?.audience_id,
              start_date: dateRange?.dateRange?.startDate,
              end_date: dateRange?.dateRange?.endDate,
            }}
            defaultSorting={[{ id: 'campaign', desc: true }]}
            fetchFn={useGetThirdPartyAudienceConversions}
          />
        </div>
      </div>
    </div>
  )
}

export default AudienceTemplate
